import { CdkDragDrop, DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, model, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatListModule } from '@angular/material/list';
import { MatTableModule } from '@angular/material/table';
import { KeyValueIsPrimary, KeyValueTypeModel } from '@models/common';
import { OperatorType } from '@models/helpers/operatorType';
import { WorkflowState } from '@models/module/workflowState';
import { AddEditComplexConditionComponent } from '@module-definition/complex-conditions/add-edit-complex-condition/add-edit-complex-condition.component';
import { ComplexConditionsListService } from '@module-definition/complex-conditions/complex-conditions-list/complex-conditions-list.service';
import { ComplexConditionGroup } from '@module-definition/complex-conditions/complex-conditions-list/complex-conditions.model';
import { TranslateModule } from '@ngx-translate/core';
import { ConfirmationDialog } from '@shared/dialogs/confirmation-dialog.component';
import { CipoFormModule } from '@shared/modules';
import { NotificationService, UserService, UtilsService } from '@shared/services';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { filter, forkJoin } from 'rxjs';

@Component({
  selector: 'cipo-complex-conditions',
  templateUrl: './complex-conditions-list.component.html',
  styleUrls: ['./complex-conditions-list.component.scss'],
  imports: [
    MatTableModule,
    CommonModule,
    TranslateModule,
    CipoFormModule,
    NgxSkeletonLoaderModule,
    MatListModule,
    DragDropModule,
    MatButtonModule,
  ],
})
export class ComplexConditionsListComponent {
  conditionalService = inject(ComplexConditionsListService);
  utilsService = inject(UtilsService);
  userService = inject(UserService);
  dialog = inject(MatDialog);
  notification = inject(NotificationService);

  loading = model<boolean>(true);
  transitionId = input<number>();
  screenId = input<number>();
  workflowId = input<number>();
  entityId = input<number>();
  conditionsChanged = output<void>();
  complexConditions = signal<ComplexConditionGroup[]>([]);
  skeleton = signal(Array(10));
  states = signal<WorkflowState[]>([]);
  templateOptions = signal<KeyValueIsPrimary<number>[]>([]);
  operatorsDict = signal<KeyValueTypeModel<number>[]>([]);
  conditionalsFieldDict = signal<KeyValueTypeModel<number>[]>([]);

  isTransitionCondition = computed(() => this.transitionId() > 0 && this.workflowId() > 0);
  isConditionalPrintout = computed(() => this.entityId() > 0 && this.screenId() > 0);

  OperatorTypeEnum = OperatorType;
  hoveredIndex: number | null = null;

  ngOnInit() {
    if (this.isConditionalPrintout()) {
      this.loadConditionalPrintout();
    } else {
      this.loadTransitionCondition();
    }
    this.conditionalService.getConditionalsFieldsDict().subscribe({
      next: response => {
        this.conditionalsFieldDict.set(response);
      },
    });

    this.conditionalService.getOperatorsDict().subscribe({
      next: response => {
        this.operatorsDict.set(response);
      },
    });
  }

  loadTransitionCondition() {
    this.loading.set(true);
    forkJoin({
      states: this.conditionalService.getStateDefinition(this.workflowId()),
      transitions: this.conditionalService.getConditionalTransitions(this.transitionId()),
    }).subscribe(({ states, transitions }) => {
      this.states.set(states);
      this.complexConditions.set(
        transitions.map(t => ({
          ...t,
          stateLabel: states.find(st => st.id === t.stateId)?.name,
        })),
      );
      this.loading.set(false);
    });
  }

  loadConditionalPrintout() {
    this.loading.set(true);
    forkJoin({
      printouts: this.conditionalService.getConditionals(this.screenId()),
      templates: this.conditionalService.getTemplateDict(this.entityId(), this.screenId()),
    }).subscribe(({ printouts, templates }) => {
      this.templateOptions.set(templates);
      this.complexConditions.set(
        printouts.map(p => ({
          ...p,
          printLabel: templates.find(tp => tp.key === p.templateId)?.value,
        })),
      );
      this.loading.set(false);
    });
  }

  dropEvent($event: CdkDragDrop<ComplexConditionGroup[]>) {
    this.utilsService.moveItemsInSignalArray(this.complexConditions, $event.previousIndex, $event.currentIndex);
    this.conditionsChanged.emit();
  }

  addEditComplexCondition(condition?: ComplexConditionGroup) {
    this.dialog
      .open(AddEditComplexConditionComponent, {
        panelClass: ['cipo-dialog', 'classic', 'cipo--height--full'],
        data: {
          condition,
          isTransitional: this.isTransitionCondition(),
          states: this.states(),
          templates: this.templateOptions(),
          operators: this.operatorsDict(),
          fieldDict: this.conditionalsFieldDict(),
          conditions: this.complexConditions(),
        },
        ...this.userService.getResponsiveDialogSize().md,
      })
      .afterClosed()
      .pipe(filter(val => !!val))
      .subscribe(updatedCondition => {
        if (!condition) {
          this.complexConditions.update(list => [...list, updatedCondition]);
        } else {
          this.complexConditions.update(list => list.map(c => (c.id === updatedCondition.id ? updatedCondition : c)));
        }
      });
  }

  deleteComplexCondition(condition: ComplexConditionGroup) {
    this.dialog
      .open(ConfirmationDialog, {
        data: {
          title: 'complexConditions.general.deleteTitleCondition',
          description: 'complexConditions.general.deleteMessageCondition',
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (!!result) {
          this.complexConditions.update(list => list.filter(c => c.id !== condition.id));
          this.conditionsChanged.emit();
          this.notification.success('complexConditions.general.deleteCondition');
        }
      });
  }
}
